import React, { useCallback, useEffect } from 'react'
import {
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Box,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { KButton } from 'components/core/KButton'
import { KDialog } from 'components/core/KDialog'
import { KDialogCloseButton } from 'components/ordering/KDialogCloseButton'
import { StoredPaymentMethod } from 'models/PaymentMethod'
import { KCreditCardNumberField } from 'components/core/KCreditCardNumberField'
import { KTextField } from 'components/core/KTextField'
import { KCreditCardValidThruField } from 'components/core/KCreditCardValidThruField'
import { KCreditCardCodeField } from 'components/core/KCreditCardCodeField'
import {
  validateCardNumber,
  validateCardExpiry,
  validateCardCode,
} from 'services'
import theme from 'core/Theme'
import { CreditCardImage } from 'components/images'
import { KSwitchField } from 'components/core/KSwitch'
import { AddCreditCardRequest } from 'apis/PaymentMethodApi'
import { useToast } from 'contexts'
import { useCreditCard } from 'hooks/CreditCardHooks'

const actionButtonSx = {
  minWidth: 320,
  height: 52,
  [theme.breakpoints.down('sm')]: {
    minWidth: 160,
    height: 40,
  },
}

interface CreditCardFormData extends AddCreditCardRequest {
  id?: string
}

export interface CreditCardEditDialogProps {
  open: boolean
  onClose: () => void
  card?: StoredPaymentMethod
  isOnlyDefaultCard?: boolean
  isNoCard?: boolean
}

const createFormState = (
  card?: StoredPaymentMethod,
  isNoCard?: boolean,
): CreditCardFormData => ({
  name: card ? `${card.cardType} ending in ${card.bogusCardNum}` : '',
  number: card ? `**** **** **** ${card.bogusCardNum}` : '',
  expiry: '',
  cvv: '',
  default: card?.default ?? (isNoCard ? true : false),
})

export const CreditCardEditDialog = ({
  open,
  onClose,
  card,
  isOnlyDefaultCard = false,
  isNoCard = false,
}: CreditCardEditDialogProps) => {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const {
    saveCreditCard,
    deleteCreditCard,
    setDefaultCard,
    isSaving,
    isDeleting,
    isSettingDefault,
  } = useCreditCard()

  const form = useForm<CreditCardFormData>({
    defaultValues: createFormState(card),
    mode: 'onChange',
    criteriaMode: 'all',
  })

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    watch,
    formState: { isDirty, errors, isValid },
    trigger,
  } = form

  useEffect(() => {
    if (open) {
      reset(createFormState(card, isNoCard))
    }
  }, [open, card, isNoCard, reset])

  const canSubmitForm = useCallback(() => {
    return isDirty && isValid
  }, [isDirty, isValid])

  const handleSaveChanges = useCallback(
    async (data: CreditCardFormData) => {
      if (!canSubmitForm()) return

      try {
        const cardData: AddCreditCardRequest = {
          name: data.name,
          number: data.number,
          expiry: data.expiry,
          cvv: data.cvv,
          default: data.default,
        }
        await saveCreditCard(cardData)
        showToast(t('Card added successfully'), 'success')
        onClose()
        reset(createFormState())
      } catch (e) {
        console.error('Failed to save credit card:', e)
        showToast(t('Failed to save card. Please try again.'), 'error')
      }
    },
    [canSubmitForm, saveCreditCard, onClose, reset, showToast, t],
  )

  const handleSubmitClick = useCallback(async () => {
    const isValid = await trigger(
      card ? ['number'] : ['name', 'number', 'expiry', 'cvv'],
    )
    if (isValid) {
      handleSubmit(handleSaveChanges)()
    }
  }, [trigger, handleSubmit, handleSaveChanges, card])

  const handleInputChange = useCallback(
    (field: keyof CreditCardFormData, value: string | boolean) => {
      setValue(field, value, { shouldDirty: true, shouldValidate: true })
    },
    [setValue],
  )

  const handleDeleteCard = useCallback(async () => {
    if (!card) return

    try {
      await deleteCreditCard(card.id)
      showToast(t('Card deleted successfully'), 'success')
      reset(createFormState())
      onClose()
    } catch (e) {
      console.error('Failed to delete credit card:', e)
      showToast(t('Failed to delete card. Please try again.'), 'error')
    }
  }, [deleteCreditCard, card, onClose, reset, showToast, t])

  const handleCloseDialog = useCallback(() => {
    clearErrors()
    onClose()
  }, [clearErrors, onClose])

  const handleSwitchChange = useCallback(
    async (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (!checked && card && isOnlyDefaultCard) {
        return
      }

      handleInputChange('default', checked)

      if (card && card.id && checked && !card.default) {
        try {
          await setDefaultCard(card.id)
          showToast(t('Default card updated successfully'), 'success')
        } catch (e) {
          console.error('Failed to set default card:', e)
          showToast(t('Failed to set default card. Please try again.'), 'error')
          handleInputChange('default', false)
        }
      }
    },
    [handleInputChange, card, setDefaultCard, showToast, t, isOnlyDefaultCard],
  )

  return (
    <KDialog open={open}>
      <DialogTitle variant="h4" sx={{ paddingX: 0 }}>
        {card ? t('Edit Card') : t('Add New Card')}
      </DialogTitle>

      <DialogContent dividers sx={{ paddingX: 0 }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 2 }}>
                <KCreditCardNumberField
                  fullWidth
                  required
                  label={`${t('Card number')}`}
                  placeholder={
                    card
                      ? `**** **** **** ${card.bogusCardNum}`
                      : '•••• •••• •••• ••••'
                  }
                  helperText={errors?.number?.message}
                  error={!!errors?.number}
                  {...register('number', {
                    required: t('Card number is required'),
                    validate: (value) =>
                      card
                        ? true
                        : validateCardNumber(value)
                          ? true
                          : t('Invalid card number'),
                  })}
                  disabled={!!card}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: !!card
                        ? 'action.disabledBackground'
                        : 'background.paper',
                    },
                  }}
                />
              </Box>

              {!card && (
                <Box sx={{ mb: 2 }}>
                  <KTextField
                    fullWidth
                    required
                    label={`${t('Name on card')}*`}
                    placeholder="NAME ON CARD"
                    helperText={errors?.name?.message}
                    error={!!errors?.name}
                    inputProps={{ maxLength: 100 }}
                    sx={{
                      '& input': { textTransform: 'uppercase' },
                    }}
                    {...register('name', {
                      required: t('Name is required'),
                    })}
                  />
                </Box>
              )}

              {!card && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <KCreditCardValidThruField
                        fullWidth
                        required
                        label={`${t('Valid thru')}*`}
                        placeholder="MM/YY"
                        helperText={errors?.expiry?.message}
                        error={!!errors?.expiry}
                        {...register('expiry', {
                          required: t('Expiration date is required'),
                          validate: (value) => validateCardExpiry(value),
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <KCreditCardCodeField
                        fullWidth
                        required
                        cardNumber={watch('number')}
                        label={`${t('CVV/CVC')}*`}
                        placeholder="***"
                        helperText={errors?.cvv?.message}
                        error={!!errors?.cvv}
                        {...register('cvv', {
                          required: t('CVV is required'),
                          validate: (value) =>
                            validateCardCode(watch('number'), value)
                              ? true
                              : t('Invalid CVV/CVC code'),
                        })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {!card && (
                <Box sx={{ mt: 3 }}>
                  <KSwitchField
                    label={t('Set as default card')}
                    checked={watch('default')}
                    onChange={handleSwitchChange}
                    disabled={
                      (card && isOnlyDefaultCard ? true : false) ||
                      (!card && isNoCard)
                    }
                  />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: { xs: 2, md: 0 },
              }}
            >
              <CreditCardImage
                style={{
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ paddingY: 2, paddingX: 0, justifyContent: 'flex-end' }}
      >
        {!!card && (
          <Box sx={{ marginRight: 'auto', paddingLeft: 2 }}>
            <KSwitchField
              label={t('Set as default card')}
              checked={watch('default')}
              onChange={handleSwitchChange}
              disabled={isSettingDefault || isOnlyDefaultCard}
            />
          </Box>
        )}
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: 'flex-end',
            textAlign: 'right',
            width: 'auto',
            marginRight: 0,
          }}
        >
          {!!card ? (
            <Grid item md={12} sm={12} xs={12}>
              <KButton
                variant="danger"
                onClick={handleDeleteCard}
                disabled={isDeleting || isSaving || isSettingDefault}
                sx={actionButtonSx}
              >
                {isDeleting ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <span>{t('Deleting...')}</span>
                  </Stack>
                ) : (
                  t('Delete Card')
                )}
              </KButton>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <KButton
                variant="primary"
                onClick={handleSubmitClick}
                disabled={isSaving || !canSubmitForm()}
                sx={actionButtonSx}
              >
                {isSaving ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <span>{t('Saving...')}</span>
                  </Stack>
                ) : (
                  t('Add Card')
                )}
              </KButton>
            </Grid>
          )}
        </Grid>
      </DialogActions>

      <KDialogCloseButton
        sx={{ width: 36, height: 36 }}
        size="small"
        onClick={handleCloseDialog}
      />
    </KDialog>
  )
}
